/**
 * Vi API client for Vibe — fetches odyssey/* read endpoints.
 *
 * Auth: VITE_VI_API_KEY is a PUBLIC-tier key embedded in the bundle.
 * Owner-tier writes (U2.4+) happen via session cookie obtained through
 * the existing vi-web /auth flow, not via an embedded key.
 */

import type { TieredText } from "@vi/odyssey";

const API_BASE = import.meta.env.VITE_VI_API_BASE;
const API_KEY = import.meta.env.VITE_VI_API_KEY;

if (!API_BASE) {
  // Surface at startup rather than first request — easier to diagnose.
  console.error("[vibe] VITE_VI_API_BASE not set; API calls will 404");
}

// ─── shared response types ────────────────────────────────────────────────

export interface RaceDTO {
  id: string;
  universeId: string;
  name: string;
  description: TieredText[];
  homeworldIds: string[];
  subtypes: unknown[];
  lifespanMin: number | null;
  lifespanMax: number | null;
  isImmortal: boolean;
  traits: TieredText[];
  abilitySystems: string[];
  category: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AbilityDTO {
  id: string;
  universeId: string;
  name: string;
  systemType: string;
  description: TieredText[];
  tiers: unknown[];
  restrictions: string[];
  weaknesses: TieredText[];
  raceCompatibility: string[];
  prerequisites: string[];
  createdAt: string;
  updatedAt: string;
}

export interface RealmDTO {
  id: string;
  universeId: string;
  name: string;
  description: TieredText[];
  type: "standard" | "pocket";
  creatorCharacterId: string | null;
  governanceCharacterId: string | null;
  parentRealmId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface WorldDTO {
  id: string;
  universeId: string;
  realmId: string;
  name: string;
  description: TieredText[];
  type: string | null;
  atmosphere: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CharacterDTO {
  id: string;
  universeId: string;
  name: string;
  phoneticName: string | null;
  aliases: unknown[];
  raceId: string | null;
  raceTruth: { raceId: string; tier: "tier_2"; notes?: string } | null;
  role: string;
  alignment: string | null;
  status: TieredText[];
  homeworldId: string | null;
  appearance: TieredText[];
  personality: TieredText[];
  weapons: unknown[];
  weaknesses: TieredText[];
  hobby: TieredText | null;
  authorCredit: string;
  storyArc: string;
  loreNotes: TieredText[];
  fullDossierNote: { tier: "tier_2"; text: string } | null;
  voiceProfile: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── fetch wrapper ────────────────────────────────────────────────────────

/** Carries the HTTP status so callers can branch (403 owner-gate, 409 conflict, …). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function getJson<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {};
  if (API_KEY) headers["x-api-key"] = API_KEY;
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers,
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} ${res.statusText} — ${text || path}`);
  }
  return (await res.json()) as T;
}

async function sendJson<T>(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  // Public key (if any) + the session cookie (credentials:include) travel together;
  // the server resolves owner vs guest. Owner-tier writes need the session cookie.
  if (API_KEY) headers["x-api-key"] = API_KEY;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const data = (await res.json()) as { error?: { message?: string } };
      if (data?.error?.message) message = data.error.message;
    } catch {
      /* non-JSON error body — keep the status line */
    }
    throw new ApiError(res.status, message);
  }
  return (res.status === 204 ? (undefined as T) : ((await res.json()) as T));
}

// ─── endpoints ────────────────────────────────────────────────────────────

export async function fetchRaces(): Promise<RaceDTO[]> {
  const { races } = await getJson<{ races: RaceDTO[] }>("/odyssey/races");
  return races;
}

export async function fetchRace(id: string): Promise<RaceDTO> {
  const { race } = await getJson<{ race: RaceDTO }>(`/odyssey/races/${id}`);
  return race;
}

export async function fetchAbilities(): Promise<AbilityDTO[]> {
  const { abilities } = await getJson<{ abilities: AbilityDTO[] }>("/odyssey/abilities");
  return abilities;
}

export async function fetchRealms(): Promise<RealmDTO[]> {
  const { realms } = await getJson<{ realms: RealmDTO[] }>("/odyssey/realms");
  return realms;
}

export async function fetchWorlds(): Promise<WorldDTO[]> {
  const { worlds } = await getJson<{ worlds: WorldDTO[] }>("/odyssey/worlds");
  return worlds;
}

export async function fetchCharacters(): Promise<CharacterDTO[]> {
  const { characters } = await getJson<{ characters: CharacterDTO[] }>("/odyssey/characters");
  return characters;
}

export async function fetchCharacter(id: string): Promise<CharacterDTO> {
  const { character } = await getJson<{ character: CharacterDTO }>(`/odyssey/characters/${id}`);
  return character;
}

// ─── writes (owner-only; U2.4) ────────────────────────────────────────────

export interface AbilityCreateInput {
  name: string;
  systemType: string;
  description?: TieredText[];
  restrictions?: string[];
  weaknesses?: TieredText[];
  prerequisites?: string[];
}

/** Create an ability. Server enforces owner role → throws ApiError(403) for guests. */
export async function createAbility(input: AbilityCreateInput): Promise<AbilityDTO> {
  const { ability } = await sendJson<{ ability: AbilityDTO }>(
    "/odyssey/abilities",
    "POST",
    input,
  );
  return ability;
}

/** Patch an ability (owner-only). */
export async function updateAbility(
  id: string,
  patch: Partial<AbilityCreateInput>,
): Promise<AbilityDTO> {
  const { ability } = await sendJson<{ ability: AbilityDTO }>(
    `/odyssey/abilities/${id}`,
    "PATCH",
    patch,
  );
  return ability;
}

/** Soft-delete an ability (owner-only). */
export async function deleteAbility(id: string): Promise<void> {
  await sendJson<void>(`/odyssey/abilities/${id}`, "DELETE");
}
